import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader, fmt } from "@/components/shared";
import { PlansBackdrop } from "@/components/PlanVisuals";
import { AvailabilityRing, ShareStat, Reveal } from "@/components/ShareVisuals";
import { SharePurchasePanel } from "@/components/SharePurchasePanel";
import { ShareDepositForm } from "@/components/ShareDepositForm";

export default function SharesPortal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/shares/me"), api.get("/settings/public"), api.get("/auth/me")]);
    setData(a.data); setSettings(b.data); setMe(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || !settings || !me) return <Loader label="Loading company shares..." />;
  const info = data.info, h = data.holding;

  return (
    <div className="space-y-6 relative" data-testid="shares-portal">
      <div className="absolute inset-x-0 -top-8 h-72 overflow-hidden pointer-events-none"><PlansBackdrop /></div>
      <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Company Shares</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Own a Stake in AI TECHNOLOGY</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">1 share = {fmt(info.share_price)} USDT. Buy any quantity subject to available inventory. All purchases are reviewed and confirmed by the platform.</p>
        </div>
        <Link to="/my-shares" data-testid="goto-my-shares" className="glass glass-hover px-5 py-2.5 rounded-full text-sm text-cyan-300 inline-flex items-center gap-2 shrink-0">My Shares <ArrowRight className="w-4 h-4" /></Link>
      </div>

      <div className="relative grid lg:grid-cols-[auto_1fr] gap-6 items-stretch">
        <Reveal className="glass rounded-2xl p-6 flex items-center gap-6">
          <AvailabilityRing available={info.available_shares} total={info.total_shares} />
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Available Shares</p>
            <p className="font-mono text-3xl font-bold text-white" data-testid="share-available">{info.available_shares.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">of {info.total_shares.toLocaleString()} total · {info.sold_shares.toLocaleString()} sold</p>
            <span className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs border ${info.sales_enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}`} data-testid="share-sales-status">
              <span className="w-1.5 h-1.5 rounded-full bg-current share-dot-pulse" />{info.sales_enabled ? "Sales open" : "Sales closed"}
            </span>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <ShareStat label="Share Price" value={info.share_price} decimals={2} suffix="USDT" testId="stat-share-price" accent="text-cyan-300" />
          <ShareStat label="My Shares" value={h.shares} testId="stat-my-shares" />
          <ShareStat label="Pending" value={h.pending_shares} testId="stat-pending-shares" accent="text-amber-300" />
          <ShareStat label="Holding Value" value={h.current_value} decimals={2} suffix="USDT" testId="stat-holding-value" accent="text-emerald-300" />
        </div>
      </div>

      <Reveal delay={0.1} className="grid lg:grid-cols-2 gap-6">
        <SharePurchasePanel info={info} balance={me.available_balance} onDone={load} />
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-heading text-lg text-white">Pricing Examples</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b border-white/5"><th className="pb-2 font-medium">Shares</th><th className="pb-2 font-medium text-right">Total (USDT)</th></tr></thead>
            <tbody>{[1, 5, 10, 100, 500].map((n) => (
              <tr key={n} className="border-b border-white/5 last:border-0"><td className="py-2.5 text-slate-200">{n} {n === 1 ? "Share" : "Shares"}</td><td className="py-2.5 font-mono text-cyan-300 text-right">{fmt(n * info.share_price)}</td></tr>
            ))}</tbody>
          </table>
          <p className="text-xs text-slate-500">Total = Number of Shares × Current Share Price. The share price is set by the platform and may change. Shares are not a guaranteed-return product; see the Risk Disclosure.</p>
        </div>
      </Reveal>

      <Reveal delay={0.15}><ShareDepositForm info={info} settings={settings} onDone={load} /></Reveal>
    </div>
  );
}
